/**
 * 结果页报告卡：列表 / 状态 / 轮询 / 下载 / 重试（文视共用）。
 */
import { computed, onUnmounted, ref, watch, type Ref } from 'vue'

export type WorkItemReportKind = 'video' | 'document'

export type ReportCardStatus =
  | 'loading'
  | 'ready'
  | 'generating'
  | 'empty'
  | 'failed'

export interface WorkItemReportSummary {
  id: number
  report_name?: string
  generation_status?: string | null
  can_download?: boolean
  file_path?: string | null
  created_at?: string | null
  generated_at?: string | null
}

export function pickLatestDownloadable(
  reports: WorkItemReportSummary[],
): WorkItemReportSummary | null {
  const downloadable = reports.filter((r) => {
    if (r.can_download) return true
    const status = (r.generation_status || '').toLowerCase()
    return status === 'completed'
  })
  if (!downloadable.length) return null
  return [...downloadable].sort((a, b) => {
    const ta = Date.parse(a.generated_at || a.created_at || '') || 0
    const tb = Date.parse(b.generated_at || b.created_at || '') || 0
    return tb - ta
  })[0]
}

export function deriveReportCardStatus(
  reports: WorkItemReportSummary[],
): ReportCardStatus {
  if (pickLatestDownloadable(reports)) return 'ready'
  const statuses = reports.map((r) => (r.generation_status || '').toLowerCase())
  if (statuses.some((s) => s === 'processing' || s === 'pending' || s === 'generating')) {
    return 'generating'
  }
  if (statuses.some((s) => s === 'failed')) return 'failed'
  return 'empty'
}

export function normalizeReportListPayload(raw: unknown): WorkItemReportSummary[] {
  if (Array.isArray(raw)) return raw as WorkItemReportSummary[]
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (Array.isArray(obj.reports)) return obj.reports as WorkItemReportSummary[]
    if (Array.isArray(obj.items)) return obj.items as WorkItemReportSummary[]
    if (Array.isArray(obj.data)) return normalizeReportListPayload(obj.data)
  }
  return []
}

export interface UseWorkItemReportCardOptions {
  workItemId: Ref<number> | number
  listReports: (workItemId: number) => Promise<unknown>
  regenerate: (workItemId: number) => Promise<unknown>
  download: (workItemId: number, reportId?: number) => Promise<void>
  pollIntervalMs?: number
  maxPolls?: number
}

export function useWorkItemReportCard(options: UseWorkItemReportCardOptions) {
  const reports = ref<WorkItemReportSummary[]>([])
  const loading = ref(false)
  const regenerating = ref(false)
  const downloading = ref(false)
  const error = ref<string | null>(null)
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let pollCount = 0

  const workItemId = computed(() =>
    typeof options.workItemId === 'number'
      ? options.workItemId
      : options.workItemId.value,
  )

  const status = computed<ReportCardStatus>(() => {
    if (loading.value && reports.value.length === 0) return 'loading'
    return deriveReportCardStatus(reports.value)
  })

  const latestReport = computed(() => pickLatestDownloadable(reports.value))

  async function refresh() {
    if (!workItemId.value) return
    loading.value = true
    error.value = null
    try {
      const raw = await options.listReports(workItemId.value)
      reports.value = normalizeReportListPayload(raw)
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      reports.value = []
    } finally {
      loading.value = false
    }
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    pollCount = 0
  }

  function startPolling() {
    stopPolling()
    const interval = options.pollIntervalMs ?? 2500
    const max = options.maxPolls ?? 40
    pollTimer = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      pollCount += 1
      await refresh()
      if (status.value !== 'generating' || pollCount >= max) {
        stopPolling()
      }
    }, interval)
  }

  async function regenerate() {
    if (!workItemId.value) return
    regenerating.value = true
    error.value = null
    try {
      await options.regenerate(workItemId.value)
      await refresh()
      if (status.value === 'generating' || status.value === 'empty') {
        startPolling()
      }
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      regenerating.value = false
    }
  }

  async function download() {
    if (!workItemId.value) return
    downloading.value = true
    error.value = null
    try {
      await options.download(workItemId.value, latestReport.value?.id)
      await refresh()
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      downloading.value = false
    }
  }

  watch(
    workItemId,
    async (id) => {
      stopPolling()
      if (!id) return
      await refresh()
      if (status.value === 'generating') startPolling()
    },
    { immediate: true },
  )

  onUnmounted(() => stopPolling())

  return {
    reports,
    status,
    latestReport,
    loading,
    regenerating,
    downloading,
    error,
    refresh,
    regenerate,
    download,
    stopPolling,
  }
}
